import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { CalendarDate, Time } from '@internationalized/date';
import { format, startOfDay } from 'date-fns';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui';
import { Calendar } from '@/components/ui/shadcn-calendar';
import { TimePickerInput } from '@/components/ui/openstatus/time-picker-input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/radix-popover';

function calendarDateToLocalDate(cd: CalendarDate): Date {
  return new Date(cd.year, cd.month - 1, cd.day);
}

function localDateToCalendarDate(d: Date): CalendarDate {
  return new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

function todayAsCalendarDate(): CalendarDate {
  return localDateToCalendarDate(new Date());
}

/** Stable calendar date for time-only state (local wall clock). */
function timeToDraftDate(t: Time): Date {
  return new Date(2000, 0, 1, t.hour, t.minute, 0, 0);
}

function draftDateToTime(d: Date): Time {
  return new Time(d.getHours(), d.getMinutes());
}

function emptyDraftDate(): Date {
  return new Date(2000, 0, 1, 0, 0, 0, 0);
}

export function ScheduleDatePicker({
  label,
  value,
  onChange,
  className,
  disabled,
  disablePastDates = false,
}: {
  label?: React.ReactNode;
  value: CalendarDate | null;
  onChange: (value: CalendarDate | null) => void;
  className?: string;
  disabled?: boolean;
  /** When true, days before today (local) cannot be selected. */
  disablePastDates?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selectedDate = value ? calendarDateToLocalDate(value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <div className={cn('flex w-full flex-col gap-1 text-sm', className)}>
        {label ? (
          <span className='text-xs font-medium text-slate-600'>{label}</span>
        ) : null}
        <PopoverTrigger asChild>
          <button
            type='button'
            disabled={disabled}
            className={cn(
              'flex min-h-10 w-full items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 text-left text-sm transition',
              'hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50',
              !value ? 'text-slate-500' : 'text-slate-900',
            )}
          >
            <span className='truncate'>
              {value
                ? format(calendarDateToLocalDate(value), 'PPP')
                : 'Pick a date'}
            </span>
            <Icon
              icon='mdi:calendar-month-outline'
              className='size-5 shrink-0 text-slate-400'
            />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent className='w-auto p-0' align='start'>
        <Calendar
          mode='single'
          selected={selectedDate}
          onSelect={(d) => {
            if (!d) {
              onChange(null);
              return;
            }
            onChange(localDateToCalendarDate(d));
            setOpen(false);
          }}
          defaultMonth={selectedDate}
          disabled={
            disablePastDates ? { before: startOfDay(new Date()) } : undefined
          }
          initialFocus
        />
        <div className='border-t border-slate-100 p-2'>
          <Button
            type='button'
            variant='light'
            color='primary'
            size='sm'
            className='w-full justify-center'
            isDisabled={disabled}
            onPress={() => {
              onChange(todayAsCalendarDate());
              setOpen(false);
            }}
          >
            Today
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ScheduleTimePicker({
  label,
  value,
  onChange,
  className,
  disabled,
}: {
  label?: React.ReactNode;
  value: Time | null;
  onChange: (value: Time | null) => void;
  className?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const hourRef = useRef<HTMLInputElement>(null);
  const minuteRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<Date>(() =>
    value ? timeToDraftDate(value) : emptyDraftDate(),
  );

  useEffect(() => {
    if (open) {
      setDraft(
        value !== null && value !== undefined
          ? timeToDraftDate(value)
          : emptyDraftDate(),
      );
    }
  }, [open, value]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const id = requestAnimationFrame(() => hourRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  const hour = value?.hour ?? 0;
  const minute = value?.minute ?? 0;

  const handleDraftUpdate = (next: Date | undefined) => {
    if (!next) {
      return;
    }
    setDraft(next);
    onChange(draftDateToTime(next));
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <div className={cn('flex w-full flex-col gap-1 text-sm', className)}>
        {label ? (
          <span className='text-xs font-medium text-slate-600'>{label}</span>
        ) : null}
        <PopoverTrigger asChild>
          <button
            type='button'
            disabled={disabled}
            className={cn(
              'flex min-h-10 w-full items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 text-left text-sm transition',
              'hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50',
              !value ? 'text-slate-500' : 'text-slate-900',
            )}
          >
            <span className='tabular-nums'>
              {value !== null && value !== undefined
                ? `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
                : 'Pick a time'}
            </span>
            <Icon
              icon='mdi:clock-outline'
              className='size-5 shrink-0 text-slate-400'
            />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent className='w-auto p-0' align='start'>
        <div className='flex items-end gap-2 px-4 pb-4 pt-3'>
          <div className='grid gap-1 text-center'>
            <span className='text-xs font-medium text-slate-600'>Hours</span>
            <TimePickerInput
              picker='hours'
              date={draft}
              setDate={handleDraftUpdate}
              ref={hourRef}
              disabled={disabled}
              onRightFocus={() => minuteRef.current?.focus()}
            />
          </div>
          <span
            className='select-none pb-2 text-lg font-medium text-slate-400'
            aria-hidden
          >
            :
          </span>
          <div className='grid gap-1 text-center'>
            <span className='text-xs font-medium text-slate-600'>Minutes</span>
            <TimePickerInput
              picker='minutes'
              date={draft}
              setDate={handleDraftUpdate}
              ref={minuteRef}
              disabled={disabled}
              onLeftFocus={() => hourRef.current?.focus()}
            />
          </div>
          <div className='flex h-9 items-center'>
            <Icon
              icon='mdi:clock-outline'
              className='ml-1 size-5 text-slate-400'
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
